import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import classNames from 'classnames';
import { Select } from 'alurkerja-ui';
import { AlurkerjaInputType } from '../../types/InputType';
import useMasterdataState from '../../state/masterdata.state';
import useTenantState from '@/states/tenant.state';
import { Label } from 'bpmn-js/lib/model/Types';

interface RenderMasterdataSelectProps {
  props: AlurkerjaInputType;
}

export const RenderMasterdataSelect = ({ props }: RenderMasterdataSelectProps) => {
  const {
    item,
    form: { control, getValues },
  } = props;

  const { masterdataRecords, fetchMasterdataRecords, isLoading } = useMasterdataState();
  const { currentTenant } = useTenantState();
  const [options, setOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [error, setError] = useState<string | null>(null);

  const defaultValue = getValues(item.name);

  useEffect(() => {
    if (item.masterdata_slug && currentTenant?.id) {
      fetchData();
    }
  }, [item.masterdata_slug, currentTenant?.id]);

  const fetchData = async () => {
    if (!item.masterdata_slug || !currentTenant?.id) return;

    setError(null);

    try {
      await fetchMasterdataRecords(item.masterdata_slug, currentTenant.id, 0, 1000);
    } catch (err: any) {
      console.error('Error fetching masterdata records:', err);
      setError(err.message || 'Failed to fetch data');
    }
  };

  useEffect(() => {
    if (masterdataRecords && Array.isArray(masterdataRecords)) {
      const formattedOptions = masterdataRecords.map((record) => ({
        value: String(getLabelValue(record, item.select_value_field || 'id')),
        label: String(getLabelValue(record, item.select_label_field || 'name')),
      }));
      console.log('Formatted Options:', formattedOptions);
      setOptions(formattedOptions);
    }
  }, [masterdataRecords, item.select_value_field, item.select_label_field]);

  const getLabelValue = (record: any, field: string) => {
    console.log('Getting label value for field:', field, 'from record:', record);
    if (!field) return '';
    const paths = field.split('.');
    let value = record;
    for (const path of paths) {
      if (value && typeof value === 'object' && path in value) {
        value = value[path];
      } else {
        return '';
      }
    }
    return value;
  };

  const getCurrentLanguage = () => {
    // You can get this from context or state management
    return 'en'; // Default to English
  };

  const getPlaceholder = () => {
    const lang = getCurrentLanguage();
    if (typeof item.placeholder === 'object' && item.placeholder !== null) {
      return item.placeholder[lang as keyof typeof item.placeholder] || (item.placeholder  as Label).en || 'Select an option';
    }
    return item.placeholder || 'Select an option';
  };

  const getLabel = () => {
    const lang = getCurrentLanguage();
    if (typeof item.label === 'object' && item.label !== null) {
      return item.label[lang as keyof typeof item.label] || item.label.en || '';
    }
    return item.label || '';
  };

  return (
    <div>
      <label
        className={classNames('font-medium text-sm', {
          'after:content-["*"] after:text-red-400 after:text-sm': item.constraints?.required,
        })}
        htmlFor={item.name}
      >
        {getLabel()}
      </label>
      <Controller
        name={item.name}
        control={control}
        defaultValue={defaultValue || ''}
        rules={{
          required: item.constraints?.required ? 'This field is required' : false,
        }}
        render={({ field, fieldState }) => {
          const selectedOption = options.find((opt) => opt.value === field.value);

          return (
            <div>
              <Select
                value={selectedOption}
                options={options}
                onChange={(e: any) => {
                  const value = e?.value || '';
                  field.onChange(value);
                }}
                isSearchable={true}
                isDisabled={item.disabled || isLoading}
                placeholder={isLoading ? 'Loading...' : error ? 'Error loading data' : getPlaceholder()}
                isLoading={isLoading}
              />
              {fieldState.error && (
                <p className="text-sm text-red-500 mt-1">{fieldState.error.message}</p>
              )}
              {error && !fieldState.error && (
                <p className="text-sm text-orange-500 mt-1">{error}</p>
              )}
              {!item.masterdata_slug && !error && (
                <p className="text-sm text-gray-500 mt-1">Please configure masterdata_slug</p>
              )}
            </div>
          );
        }}
      />
    </div>
  );
};
